'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export interface ViewData {
  count: number | null;
  isAdmin: boolean;
}

const ViewCountContext = createContext<ViewData | null>(null);

const SESSION_KEY = 'portfolio-view-tracked';

let trackPromise: Promise<ViewData> | null = null;

async function fetchViewData(): Promise<ViewData> {
  const alreadyTracked =
    typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === '1';

  const response = await fetch('/api/views', {
    method: alreadyTracked ? 'GET' : 'POST',
  });

  if (!response.ok) {
    throw new Error('Failed to track view');
  }

  const data = (await response.json()) as ViewData;

  if (!alreadyTracked && typeof window !== 'undefined') {
    sessionStorage.setItem(SESSION_KEY, '1');
  }

  return data;
}

function trackPageViewOnce(): Promise<ViewData> {
  if (!trackPromise) {
    trackPromise = fetchViewData().catch((error) => {
      trackPromise = null;
      console.error('Error tracking visitor count:', error);
      return { count: null, isAdmin: false };
    });
  }

  return trackPromise;
}

export function ViewCountProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<ViewData | null>(null);

  useEffect(() => {
    let isMounted = true;

    trackPageViewOnce().then((viewData) => {
      if (isMounted) {
        setData(viewData);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <ViewCountContext.Provider value={data}>{children}</ViewCountContext.Provider>
  );
}

export function useViewCount() {
  return useContext(ViewCountContext);
}
